import * as BaseSentry from '@sentry/vue'
import { makeMultiplexedTransport, EnvelopeItemType } from '@sentry/core'

/**
 * Returns the DSN to use for a project. Should check the current
 * environment and decide which DSN to use.
 */
type DsnFunc = () => string

/**
 * Custom initialization args for Sentry
 */
export type InitArgs = {
  project: string
}

/**
 * This type extends the existing Sentry.init function type to also
 * allow our custom InitArgs to be given in the first argument.
 */
// We spread out the original arguments, split into the first and the
// rest, and then add to the first and put it all back together.
// https://stackoverflow.com/a/52294058
type InitFunc = typeof BaseSentry.init extends (...args: [infer T, ...infer U]) => infer R ? (...args: [T & InitArgs, ...U]) => R : never;

/**
 * Settings for an individual project. These are used when needed: Some
 * at Sentry.init() and some when sending the individual message.
 *
 * @field dsn {function} A function that returns the DSN to use.
 * @field sampleRate {number} The sample rate for events from this
 * project. This does not override the global sample rate given to
 * Sentry.init(), so set the project sample rate accordingly.
 */
export type ProjectSettings = {
  dsn: DsnFunc
  sampleRate?: number
  allowUrls?: Array<string | RegExp>
  tracePropagationTargets?: Array<string | RegExp>
}

/**
 * A mapping of "sentryProject" to settings to use. The event tags are
 * used to find a project to route to. It falls back to a default DSN
 * set up via Sentry.init().
 * Add DSNs using {@link wrapSentryForProject}.
 */
const projectSettings: { [key: string]: ProjectSettings } = {}

/**
 * Wrap the standard Sentry functions to route all messages to the given
 * DSN.
 * Should be used to build a project-level `sentry.ts` file which
 * exports the output of this function, like:
 *
 *    import * as Sentry from '@grantstreet/sentry-multiplex'
 *    export wrapSentryForProject(
 *      'project-name',
 *      {
 *        dsn: () => isProd() ? '...' : '...',
 *      },
 *    )
 */
const wrapSentryForProject: (project: string, settings: ProjectSettings) => (typeof BaseSentry & { init: InitFunc }) = (project, settings) => {
  projectSettings[project] = settings

  // Everything project-specific should be scoped
  const setupProjectScope = (scope: BaseSentry.Scope) => {
    // The tag determines the DSN later, using projectDsnForMessage
    scope.setTag('project', project)

    // If our project has set a sampleRate, we need to handle it
    // ourselves. The default, global sample rate will not do it for us.
    // Described in Sentry's Zendesk: https://sentry.zendesk.com/hc/en-us/articles/33557209673499-Can-I-set-different-sample-rates-for-different-errors
    // The default, global sample rate will _also_ be used, though...
    const sampleRate = projectSettings[project]?.sampleRate
    if (sampleRate) {
      scope.addEventProcessor(
        (event) => {
          return Math.random() < sampleRate ? event : null
        },
      )
    }
  }

  return {
    ...BaseSentry,
    // Wrap the init() function to set the fall back DSN from the given
    // project settings. This means we don't need to specify the DSN
    // again.
    init: (args) => {
      args ??= {}
      args.dsn ??= settings.dsn()
      args.project ??= project
      return init(args)
    },

    // We need to wrap every function that could send a message to make
    // sure we set the scope correctly.
    captureException: (msg, hint) => BaseSentry.withScope(scope => {
      setupProjectScope(scope)
      return BaseSentry.captureException(msg, hint)
    }),
    captureEvent: (msg, hint) => BaseSentry.withScope(scope => {
      setupProjectScope(scope)
      return BaseSentry.captureEvent(msg, hint)
    }),
    captureMessage: (msg, hint) => BaseSentry.withScope(scope => {
      setupProjectScope(scope)
      return BaseSentry.captureMessage(msg, hint)
    }),
  }
}

type getEventFunc = (types?: EnvelopeItemType[]) => BaseSentry.Event | undefined
const projectDsnForMessage = ({ getEvent }: { getEvent: getEventFunc }) => {
  // We want to do this for as many event envelope types as we want to
  // attach to specific projects. These event types are the ones that
  // make sense to attach to specific projects. All other event types
  // will be sent with the main DSN set by Sentry.init().
  // See https://develop.sentry.dev/sdk/data-model/envelope-items/ for
  // a list of envelope types valid for getEvent().
  const event = getEvent(['event', 'transaction', 'replay_event', 'feedback'])

  if (typeof event?.tags?.project === 'string' && projectSettings[event.tags.project]) {
    return [projectSettings[event.tags.project].dsn()]
  }

  return []
}

/**
 * Initialize Sentry. This must only be called once by the main app
 * package.
 *
 * Extends original Sentry.init() to set up multiplex transport based on
 * projects registered with the wrapSentryForProject function.
 */
const init: InitFunc = (args) => {
  // Set a default tag so that events get routed to the main project by default
  BaseSentry.setTag('project', args.project)

  // Always ignore certain errors to preserve our Sentry quotas
  args.ignoreErrors ||= []
  args.ignoreErrors.push(...[
    /PADDINGX' is undefined$/,
    'The operation was canceled by the user.',
    /QuotaExceededError/,
  ])

  // Set some defaults for events. This can be overridden by the
  // incoming args.
  // To add more processing to events before this, use
  // Sentry.addEventProcessor.
  args.beforeSend ??= (event, _) => {
    // Set a base value for some tags. This allows us to use them in
    // server-side issue grouping rules
    event.tags ??= {}
    // eslint-disable-next-line dot-notation
    event.tags['error_code'] ??= 'none'

    return event
  }

  // Do not capture traces by default
  const tracesSampleRate = 0.0

  // process.env.SENTRY_RELEASE_ID is injected by our webpack configs
  args.release ||= process.env.SENTRY_RELEASE_ID
  return BaseSentry.init({
    tracesSampleRate,
    ...args,
    tracePropagationTargets: [
      // Trace propagation targets should be added as backends are set
      // up to participate in distributed traces. If a backend is sent
      // the tracing HTTP headers without being ready for them, they
      // will refuse the CORS pre-flight request because it has headers
      // not allowed by Access-Control-Allow-Headers (the 'baggage',
      // 'traceparent', and 'sentry-trace' headers).
      // If your project is using wrapSentryForProject, it
      // can set its trace propagation targets there before this init is
      // called.

      // Addresses (delivery-method) service (PSC-21508)
      /https?:\/\/[^:/]+\.grantstreet\.com(?::\d+)?\/svc\/addresses/,
      /https?:\/\/([^:/]+\.)?(govhub\.com|pay-hub\.net)\/svc\/addresses/,

      // Config service (PSC-21509)
      /https?:\/\/[^.]+config\.grantstreet\.com(?::\d+)?/,

      // Cart service (PSC-21510)
      /https?:\/\/[^:/]+\.grantstreet\.com(?::\d+)?\/svc\/cart/,
      /https?:\/\/([^:/]+\.)?(govhub\.com|pay-hub\.net)\/svc\/cart/,

      // Ebilling service (PSC-21511)
      /https?:\/\/[^:/]+\.grantstreet\.com(?::\d+)?\/svc\/ebilling/,
      /https?:\/\/([^:/]+\.)?(govhub\.com|pay-hub\.net)\/svc\/ebilling/,

      // Add individual project targets
      ...(Object.values(projectSettings).map((setting) => setting.tracePropagationTargets ?? []).flat()),
      // And any added by the init() call
      ...(args.tracePropagationTargets ?? []),
    ],
    transport: makeMultiplexedTransport(args.transport ?? BaseSentry.makeFetchTransport, projectDsnForMessage),
  })
}

// Export everything we don't override from Sentry. We override the things that
// need project-specific handling. The goal is to exactly
// match the signatures of the standard Sentry functions. The Sentry
// documentation should Just Work™.
const sentry: Omit<typeof BaseSentry, 'init'> & {init: InitFunc} = {
  ...BaseSentry,
  init,
}
export default sentry

// Additionally export our wrapper function if asked
export {
  wrapSentryForProject,
}
